/**
 * <homepage-collection-carousel> — v4 single-row collection switcher.
 *
 * Sibling of homepage-collection-experience.js (v3), not a replacement: v3
 * hydrates a wrapping GRID and owns a visible sub-filter row, this one hydrates
 * a single horizontally-scrolling ROW under one fixed filter. Shared model:
 * fetch the collection's own section HTML with `?section_id=` and reuse its
 * cards, so filtering matches the collection page exactly.
 *
 * What's different, and why:
 *  - The fixed filter (data-fixed-query) rides on EVERY request. Category pills
 *    filter within it.
 *  - Cards are POOLED, not all inserted. One fetch returns the collection's full
 *    page (36), we insert `initial-count` and hold the rest in memory, appending
 *    a batch whenever the row is scrolled near its end. Un-inserted cards cost
 *    zero image bytes, which is the whole point of "load less" — and appends up
 *    to the 36th product need no network at all. Past that we page.
 *  - No pagination UI: the pager is only read to learn whether a next page
 *    exists.
 */
// The v4 section emits this <script> once per section instance and the homepage
// runs two of them, so this file executes more than once per page. Every
// declaration therefore lives inside this guard: re-running a classic script
// that declares a top-level let/const/class throws "Identifier '...' has
// already been declared" before any of it runs. Only the first run gets in.
if (!customElements.get('homepage-collection-carousel')) {
  // Fallback discriminator for the id namespace, used only if an instance sits
  // outside a #shopify-section-* wrapper. Never resets, so instances can't collide.
  let hccInstanceSeq = 0;

  class HomepageCollectionCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.viewport || !this.track) return;

      this.carousel = this.querySelector('[data-carousel]');
      this.statusEl = this.querySelector('[data-carousel-status]');
      this.emptyEl = this.querySelector('[data-carousel-empty]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.tabs = Array.from(this.querySelectorAll('[data-quick-filter-btn]'));

      this.activeClass = 'is-active';
      this.primaryUrl = this.dataset.primaryCollectionUrl || '';
      this.collectionSectionKey = this.dataset.collectionSectionId || 'product-grid';
      this.fixedQuery = this.dataset.fixedQuery || '';
      this.fixedQueryAllOverride = this.dataset.fixedQueryAllOverride || '';
      this.initialCount = parseInt(this.dataset.initialCount, 10) || 8;
      this.batchCount = parseInt(this.dataset.batchCount, 10) || 4;
      this.cardsMobile = parseInt(this.dataset.cardsMobile, 10) || 2;
      this.cardsDesktop = parseInt(this.dataset.cardsDesktop, 10) || 4;

      // Unique per section instance — prefixes every id we inject. See extractCards().
      const sectionWrapper = this.closest('[id^="shopify-section-"]');
      this.idNamespace = (sectionWrapper && sectionWrapper.id) || `hcc-${(hccInstanceSeq += 1)}`;

      this.docCache = new Map();
      this.sectionIds = new Map();
      this.sectionIdPromises = new Map();
      this.requestCounter = 0;
      this.swapGeneration = 0;
      this.abortController = null;

      // Cards fetched but not yet inserted — the buffer the scroll appends drain.
      this.pool = [];
      this.baseParams = '';
      this.currentPage = 1;
      this.hasMore = false;
      this.loadingMore = false;
      this.fillDepth = 0;

      this.handleTabClick = this.onTabClick.bind(this);
      this.handleScroll = this.onScroll.bind(this);
      this.handleResize = this.onResize.bind(this);
      this.handlePrev = this.onArrow.bind(this, -1);
      this.handleNext = this.onArrow.bind(this, 1);

      this.tabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      // Widening the viewport fits more cards, which can leave the row no longer
      // overflowing — and a row that can't scroll emits no scroll event, so the
      // auto-load would have no way to fire again (on mobile there aren't even
      // arrows to escape with). Re-run the fill check on resize/rotate.
      window.addEventListener('resize', this.handleResize, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);

      // Belt and braces: the section resolves the starting pill server-side, but
      // if a saved config somehow renders none as active, fall back rather than
      // leaving the row with nothing bold. getActiveTab() already resolves
      // active → default → first, and setActiveTab is idempotent.
      if (this.tabs.length) this.setActiveTab(this.getActiveTab());

      if (this.primaryUrl) this.ensureSectionId(this.primaryUrl);
      this.syncArrows();
      this.reconcileInitialRow();
    }

    disconnectedCallback() {
      // connectedCallback bails before assigning any of this when the markup is
      // incomplete, so nothing here may assume it ran to completion.
      if (!this.tabs) return;
      this.tabs.forEach((tab) => tab.removeEventListener('click', this.handleTabClick));
      if (this.viewport) this.viewport.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.abortController) this.abortController.abort();
      clearTimeout(this.fillTimer);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.resizeTimer);
      clearTimeout(this.releaseTimer);
    }

    /* ---- state helpers --------------------------------------------------- */

    getActiveTab() {
      return (
        this.tabs.find((tab) => tab.classList.contains(this.activeClass)) ||
        this.tabs.find((tab) => tab.dataset.default === 'true') ||
        this.tabs[0]
      );
    }

    visibleCards() {
      return window.matchMedia('(min-width: 750px)').matches ? this.cardsDesktop : this.cardsMobile;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // The fixed filter + the active category. `query_params_all_override` keeps its
    // v3 meaning: it replaces the fixed query while the default/ALL pill is active
    // (e.g. New Arrivals restricted to a few categories on ALL).
    //
    // "ALL" means a pill that filters nothing, not the pill marked default. The
    // Liquid falls back to the first visible pill as the default, so with ALL
    // disabled JEANS was the default, picked up the override, and its categories
    // OR'd with Jeans: New Arrivals + JEANS showed shorts and corduroy (2026-10-13).
    buildParams(tab) {
      const params = new URLSearchParams();
      let tabFilters = false;

      if (tab) {
        new URLSearchParams(tab.dataset.query || '').forEach((value, key) => {
          if (value === '') return;
          params.append(key, value);
          if (key.startsWith('filter.')) tabFilters = true;
        });
      }

      const useOverride = tab && !tabFilters && this.fixedQueryAllOverride;
      const fixed = useOverride ? this.fixedQueryAllOverride : this.fixedQuery;
      new URLSearchParams(fixed || '').forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });

      return params.toString();
    }

    setActiveTab(activeTab) {
      if (!activeTab) return;
      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle(this.activeClass, isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    setBusy(isBusy) {
      if (!this.carousel) return;
      this.carousel.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      this.carousel.classList.toggle('is-swapping', isBusy);
    }

    toggleEmpty(isEmpty) {
      if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
      if (this.carousel) this.carousel.classList.toggle('is-empty', isEmpty);
    }

    announce(count, label) {
      if (!this.statusEl) return;
      const suffix = label ? ` in ${label}` : '';
      this.statusEl.textContent = `${count} ${count === 1 ? 'product' : 'products'}${suffix}`;
    }

    /* ---- events ---------------------------------------------------------- */

    onTabClick(event) {
      event.preventDefault();
      const tab = event.currentTarget;
      if (tab.classList.contains(this.activeClass)) return;

      this.setActiveTab(tab);
      this.baseParams = this.buildParams(tab);
      this.loadCategory(this.baseParams, { showLoading: true });
    }

    onScroll() {
      if (this.scrollTimer) return;
      // Plain setTimeout throttle rather than rAF: rAF is suspended on hidden
      // tabs (and in headless panes), which would strand the auto-load.
      this.scrollTimer = setTimeout(() => {
        this.scrollTimer = null;
        this.syncArrows();
        if (this.needsMore()) this.fill();
      }, 100);
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.syncArrows();
        this.fill();
      }, 150);
    }

    onArrow(direction) {
      const amount = Math.round(this.viewport.clientWidth * 0.9) * direction;
      this.viewport.scrollBy({
        left: amount,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    syncArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      const remaining = this.viewport.scrollWidth - this.viewport.clientWidth - this.viewport.scrollLeft;
      if (this.prevBtn) this.prevBtn.disabled = this.viewport.scrollLeft <= 2;
      if (this.nextBtn) {
        this.nextBtn.disabled = remaining <= 2 && !this.pool.length && !this.hasMore;
      }
    }

    /* ---- fetching -------------------------------------------------------- */

    // Sections inside JSON templates carry a prefixed id (`template--<id>__<key>`)
    // we can't know from the homepage, so fetch the collection page once and read
    // it off the grid. Memoized.
    ensureSectionId(collectionUrl) {
      if (this.sectionIds.has(collectionUrl)) return Promise.resolve(this.sectionIds.get(collectionUrl));
      if (this.sectionIdPromises.has(collectionUrl)) return this.sectionIdPromises.get(collectionUrl);

      const promise = (async () => {
        try {
          const response = await fetch(collectionUrl);
          const text = await response.text();
          const match = text.match(/id="product-grid"\s+data-id="([^"]+)"/);
          const sectionId = (match && match[1]) || this.collectionSectionKey;
          this.sectionIds.set(collectionUrl, sectionId);
          return sectionId;
        } catch (e) {
          return this.collectionSectionKey;
        }
      })();

      this.sectionIdPromises.set(collectionUrl, promise);
      return promise;
    }

    async fetchSection(params, page) {
      const sectionId = await this.ensureSectionId(this.primaryUrl);
      const search = new URLSearchParams(params || '');
      search.set('section_id', sectionId);
      if (page > 1) search.set('page', String(page));

      const url = `${this.primaryUrl}?${search.toString()}`;
      if (this.docCache.has(url)) return this.docCache.get(url);

      const response = await fetch(url, {
        signal: this.abortController ? this.abortController.signal : undefined,
      });
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      if (this.docCache.size >= 20) this.docCache.delete(this.docCache.keys().next().value);
      this.docCache.set(url, doc);
      return doc;
    }

    // Always CLONE: the parsed document is cached, and inserting its nodes would
    // move them out of the cache, emptying it for the next visit to that category.
    extractCards(doc) {
      const grid = doc.querySelector('#ProductGridContainer ul#product-grid') || doc.querySelector('ul#product-grid');
      if (!grid) return [];
      return Array.from(grid.children)
        .filter((node) => node.tagName === 'LI')
        .map((node) => {
          const clone = node.cloneNode(true);
          this.namespaceIds(clone);
          return clone;
        });
    }

    /**
     * a11y (WCAG 4.1.1 / axe `duplicate-id`). Same defect and same fix as
     * homepage-collection-experience.js `namespaceIds()` — keep the two in sync.
     *
     * Cards come from the COLLECTION section's render, so their ids carry the
     * collection template's section id, not ours. The v19 homepage runs two v4
     * carousels plus a v3 and a v1 switcher off overlapping collections, so the
     * same product lands more than once and its ids collide verbatim.
     *
     * Runs on the CLONE, never the cached node (see extractCards' contract), so
     * it stays idempotent across repeat visits to a category — the cached doc is
     * always pristine and each clone is prefixed exactly once.
     */
    namespaceIds(root) {
      const prefix = `${this.idNamespace}--`;
      const renamed = new Map();

      // The card <li> can carry an id itself, so include the root, not just its
      // descendants (querySelectorAll never matches the element it's called on).
      const withId = Array.from(root.querySelectorAll('[id]'));
      if (root.hasAttribute && root.hasAttribute('id')) withId.unshift(root);

      withId.forEach((el) => {
        const current = el.getAttribute('id');
        if (!current || current.startsWith(prefix)) return;
        const next = prefix + current;
        renamed.set(current, next);
        el.setAttribute('id', next);
      });
      if (!renamed.size) return;

      const IDREF_ATTRS = [
        'for',
        'form',
        'headers',
        'list',
        'aria-labelledby',
        'aria-describedby',
        'aria-controls',
        'aria-owns',
      ];
      const scoped = Array.from(root.querySelectorAll('*'));
      if (root.nodeType === 1) scoped.unshift(root);
      scoped.forEach((el) => {
        IDREF_ATTRS.forEach((attr) => {
          if (!el.hasAttribute(attr)) return;
          const mapped = el
            .getAttribute(attr)
            .split(/\s+/)
            .filter(Boolean)
            .map((token) => renamed.get(token) || token)
            .join(' ');
          el.setAttribute(attr, mapped);
        });
        if (el.tagName === 'A') {
          const href = el.getAttribute('href') || '';
          if (href.startsWith('#') && renamed.has(href.slice(1))) {
            el.setAttribute('href', `#${renamed.get(href.slice(1))}`);
          }
        }
      });
    }

    // Dawn's pagination snippet gives the NEXT link the `--prev` class and vice
    // versa, so key off page numbers in the hrefs instead of class names or
    // locale-dependent labels.
    docHasNextPage(doc, currentPage) {
      const links = doc.querySelectorAll('.pagination__list a[href]');
      for (let i = 0; i < links.length; i++) {
        const href = links[i].getAttribute('href') || '';
        let page = 0;
        try {
          page = parseInt(new URL(href, window.location.origin).searchParams.get('page') || '0', 10);
        } catch (e) {
          page = 0;
        }
        if (page > currentPage) return true;
      }
      return false;
    }

    /* ---- rendering ------------------------------------------------------- */

    // On connect: the server-rendered row can't apply the fixed filter (that needs
    // the collection's own render), so reconcile it. Silent — no overlay, and no
    // swap at all when the product set already matches.
    reconcileInitialRow() {
      const tab = this.getActiveTab();
      this.baseParams = this.buildParams(tab);
      this.loadCategory(this.baseParams, { showLoading: false, preserveIfSame: true });
    }

    async loadCategory(params, { showLoading = true, preserveIfSame = false } = {}) {
      if (!this.primaryUrl) return;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestCounter;
      const generation = ++this.swapGeneration;
      this.pool = [];
      this.loadingMore = false;

      if (showLoading) this.setBusy(true);

      try {
        const doc = await this.fetchSection(params, 1);
        if (requestId !== this.requestCounter) return;

        const cards = this.extractCards(doc);
        this.currentPage = 1;
        this.hasMore = cards.length > 0 && this.docHasNextPage(doc, 1);

        const activeTab = this.getActiveTab();
        const label = activeTab ? activeTab.dataset.label || '' : '';

        // A category with nothing under the fixed filter would otherwise clear the
        // row to a silent blank strip.
        this.toggleEmpty(cards.length === 0);

        // Server render already shows these products — keep the painted DOM and
        // just prime the pool behind it.
        if (preserveIfSame && this.matchesRenderedCards(cards)) {
          this.pool = cards.slice(this.track.children.length);
          this.setBusy(false);
          this.syncArrows();
          this.fill();
          return;
        }

        const initial = cards.slice(0, this.initialCount);
        this.pool = cards.slice(initial.length);
        this.prepareCards(initial, this.visibleCards());

        await this.preloadAndDecode(initial, showLoading ? 1500 : 600);
        if (generation !== this.swapGeneration) return;

        this.applyCards(initial);
        this.setBusy(false);
        this.announce(cards.length, label);
        this.syncArrows();
        this.fill();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('homepage-collection-carousel: category fetch failed', error);
        if (requestId === this.requestCounter) this.setBusy(false);
      }
    }

    // Compare what's painted against the fetched set, by product URL.
    matchesRenderedCards(cards) {
      const rendered = Array.from(this.track.children);
      if (!rendered.length || cards.length < rendered.length) return false;
      for (let i = 0; i < rendered.length; i++) {
        if (this.cardKey(rendered[i]) !== this.cardKey(cards[i])) return false;
      }
      return true;
    }

    cardKey(li) {
      const link = li.querySelector('a[href]');
      if (!link) return '';
      return (link.getAttribute('href') || '').split('?')[0];
    }

    // Pre-insertion prep. Cancelling the reveal animation before the node enters
    // the live DOM is what stops Safari painting the initial animation state; the
    // eager/sync flip is the iOS fix for lazy images in swapped-in DOM (iOS defers
    // those loads until after first paint, so each card flashes white).
    prepareCards(nodes, eagerCount) {
      nodes.forEach((li, index) => {
        li.classList.add('scroll-trigger--cancel');
        li.removeAttribute('data-cascade');
        if (index >= eagerCount) return;
        const img =
          li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img');
        if (img) {
          img.setAttribute('loading', 'eager');
          img.setAttribute('decoding', 'sync');
        }
      });
    }

    // Warm the browser's decoded-image cache with bare clones (same srcset/sizes →
    // same candidate the real cards pick) so the row reveals already-painted
    // instead of blank-then-image. Mirrors v3 / FacetFiltersForm.preloadAboveFold.
    preloadAndDecode(nodes, capMs) {
      return new Promise((resolve) => {
        const sources = nodes
          .slice(0, this.visibleCards() + 1)
          .map(
            (li) =>
              li.querySelector('.card-image-slider__list .slider__slide:first-child img') || li.querySelector('img')
          )
          .filter(Boolean);

        if (!sources.length) {
          resolve();
          return;
        }

        const stage = document.createElement('div');
        stage.setAttribute('aria-hidden', 'true');
        stage.style.cssText =
          'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';

        const clones = sources.map((src) => {
          const img = document.createElement('img');
          const srcset = src.getAttribute('srcset');
          const sizes = src.getAttribute('sizes');
          if (srcset) img.setAttribute('srcset', srcset);
          if (sizes) img.setAttribute('sizes', sizes);
          img.setAttribute('src', src.getAttribute('src') || '');
          img.loading = 'eager';
          stage.appendChild(img);
          return img;
        });
        document.body.appendChild(stage);

        const done = () => {
          if (stage.parentNode) stage.remove();
          resolve();
        };
        if (typeof clones[0].decode !== 'function') {
          done();
          return;
        }
        const decoded = Promise.all(clones.map((img) => img.decode().catch(() => {})));
        const cap = new Promise((r) => setTimeout(r, capMs));
        Promise.race([decoded, cap]).then(done);
      });
    }

    applyCards(nodes) {
      // Reserve the outgoing height so a shorter incoming category can't collapse
      // the row and shift the page. min-height is a floor, so a taller category
      // just overflows it. Released on a timer (not rAF, which is suspended on
      // hidden tabs and would leave the reserve stuck).
      const reserve = this.viewport.offsetHeight;
      if (reserve > 0) this.viewport.style.minHeight = `${reserve}px`;

      // Drop the dim BEFORE inserting so the new row appears at full opacity in
      // one paint, with no fade-in.
      if (this.carousel) this.carousel.classList.remove('is-swapping');
      this.track.replaceChildren.apply(this.track, nodes);
      this.viewport.scrollLeft = 0;

      clearTimeout(this.releaseTimer);
      this.releaseTimer = setTimeout(() => {
        this.viewport.style.minHeight = '';
      }, 400);
    }

    /* ---- auto-load ------------------------------------------------------- */

    // True when there's less than three-quarters of a viewport of unseen row left
    // and something to add. Covers both "hasn't overflowed yet" (fill on load) and
    // "user scrolled near the end".
    needsMore() {
      if (!this.pool.length && !this.hasMore) return false;
      const remaining = this.viewport.scrollWidth - this.viewport.clientWidth - this.viewport.scrollLeft;
      return remaining < this.viewport.clientWidth * 0.75;
    }

    fill() {
      clearTimeout(this.fillTimer);
      this.fillTimer = setTimeout(() => {
        if (!this.needsMore()) {
          this.fillDepth = 0;
          this.syncArrows();
          return;
        }
        // Backstop against a pathological loop (e.g. zero-width viewport in a
        // headless pane, where `remaining` never grows).
        if (this.fillDepth >= 20) {
          this.fillDepth = 0;
          return;
        }
        this.fillDepth += 1;
        this.loadMore().then((added) => {
          if (added) this.fill();
          else {
            this.fillDepth = 0;
            this.syncArrows();
          }
        });
      }, 0);
    }

    async loadMore() {
      if (this.loadingMore) return false;

      if (this.pool.length) {
        this.appendBatch();
        return true;
      }
      if (!this.hasMore) return false;

      this.loadingMore = true;
      const generation = this.swapGeneration;
      try {
        const nextPage = this.currentPage + 1;
        const doc = await this.fetchSection(this.baseParams, nextPage);
        // A category switch landed while this was in flight — its results belong
        // to the old row.
        if (generation !== this.swapGeneration) return false;

        const cards = this.extractCards(doc);
        this.currentPage = nextPage;
        this.hasMore = cards.length > 0 && this.docHasNextPage(doc, nextPage);
        if (!cards.length) return false;

        this.pool.push.apply(this.pool, cards);
        this.appendBatch();
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('homepage-collection-carousel: page fetch failed', error);
          if (generation === this.swapGeneration) this.hasMore = false;
        }
        return false;
      } finally {
        this.loadingMore = false;
      }
    }

    appendBatch(count) {
      const batch = this.pool.splice(0, count || this.batchCount);
      if (!batch.length) return 0;
      // Appended cards are at most three-quarters of a viewport ahead of where the
      // shopper already is, so load them eagerly rather than letting lazy loading
      // pop them in under the finger.
      this.prepareCards(batch, batch.length);
      this.track.append.apply(this.track, batch);
      return batch.length;
    }

    /* ---- category pill visibility ----------------------------------------
       Deliberately NOT done from facet counts. Two live-verified reasons
       (jnco.com, 2026-07-31, fixed filter = New Arrivals):

       1. It cannot work for a sort-based fixed filter. `sort_by` doesn't filter,
          so every category's facet count is identical with and without it
          (Accessories = 11 either way) — there is no "has no new arrivals"
          signal in the facets to read.
       2. It actively breaks navigation for a cross-param pill. Shopify narrows
          a facet value's count by the OTHER active params, so the tag-based
          CARGO pill read 9 clean → 6 under the ALL override → 2 with JEANS
          active. Same-param siblings keep their true counts, so only the
          odd-param-out pill decays; once it hits 0 Shopify omits the value
          entirely and the pill would be hidden the moment a shopper clicks
          another category, with no way back to it.

       Incompatible categories are instead dropped server-side from the pill's
       "Not compatible with" (`hide_subfilters`) list — deterministic, merchant-
       controlled, no fetch, and no flash. See the same trap in CLAUDE.md
       ("collection.filters counts are narrowed by the URL's active filters"). */
  }

  customElements.define('homepage-collection-carousel', HomepageCollectionCarousel);
}
